import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Camera, Cloud, CloudOff, Crosshair, FileText, Loader2, MapPin, Mic, Square, Trash2 } from 'lucide-react';
import { Button, Card, Chip } from '@/ui/primitives';
import { Stack } from '@/ui/layout';
import { fetchCurrentUser } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { useVisitaBundle, useSyncVisita, visitaRelatorioPdfUrl } from '../api';
import { estimarEstadio, montarLegenda } from '../phenology';
import { tirarFoto, pegarLocalizacao, resolverTalhao, estaOnline, onConnectivityChange } from '../capture';
import { addFoto, updateFoto, removeFoto, upsertVisita, addAmostra, type PendingVisita, type Medicao } from '../offline';
import { iniciarGravacao, suportaGravacao, type GravacaoAtiva } from '../audio';
import { AmostraForm } from './AmostraForm';

interface FotoItem {
  clientUuid: string;
  url: string;
  talhaoNumero: string | null;
  legenda: string[];
  estadio: string | null;
  recomendacao: string;
  accuracy: number | null;
  hasAudio: boolean;
  audioDurMs: number | null;
  synced: boolean;
}

interface AmostraItem {
  clientUuid: string;
  talhaoNumero: string | null;
  nContagens: number;
  accuracy: number | null;
  synced: boolean;
}

interface PontoPendente {
  lat: number | null;
  lon: number | null;
  accuracy: number | null;
  talhaoId: number | null;
  talhaoNumero: string | null;
}

const textareaCls = cn(
  'w-full rounded-md bg-elevated border-[1.5px] border-border2 px-3 py-2 text-sm text-text',
  'placeholder:text-text-muted shadow-[0_1px_2px_rgba(0,0,0,0.04)] resize-y',
  'transition-[border-color,box-shadow] focus:outline-none focus:border-[var(--accent)]',
  'focus:shadow-[0_0_0_3px_rgba(233,106,69,0.15)]',
);

export function CapturaVisita({ imovelId }: { imovelId: number }) {
  const bundleQ = useVisitaBundle(imovelId);
  const { data: me } = useQuery({ queryKey: ['auth', 'me'], queryFn: ({ signal }) => fetchCurrentUser(signal), staleTime: 5 * 60_000 });
  const sync = useSyncVisita();

  const [visitaUuid] = useState(() => crypto.randomUUID());
  const [dataVisita] = useState(() => new Date().toISOString());
  const [observacoes, setObservacoes] = useState('');
  const [fotos, setFotos] = useState<FotoItem[]>([]);
  const [amostras, setAmostras] = useState<AmostraItem[]>([]);
  const [amostraOpen, setAmostraOpen] = useState(false);
  const [pontoPendente, setPontoPendente] = useState<PontoPendente | null>(null);
  const [online, setOnline] = useState(true);
  const [capturing, setCapturing] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [gravandoUuid, setGravandoUuid] = useState<string | null>(null);
  const recRef = useRef<GravacaoAtiva | null>(null);
  const persisted = useRef(false);
  const catalogo = bundleQ.data?.catalogo ?? null;

  useEffect(() => {
    void estaOnline().then(setOnline);
    return onConnectivityChange(setOnline);
  }, []);

  const fazenda = bundleQ.data?.fazenda ?? null;
  const pendentes = fotos.filter((f) => !f.synced).length;
  const amostrasPendentes = amostras.filter((a) => !a.synced).length;

  function buildVisita(): PendingVisita {
    return {
      clientUuid: visitaUuid,
      imovelId,
      fazendaNome: fazenda?.fazenda ?? null,
      produtorNome: fazenda?.produtor ?? null,
      agronomoNome: me?.name ?? null,
      dataVisita,
      observacoes,
      status: 'pendente',
      createdAt: Date.now(),
      syncedVisitaId: null,
    };
  }

  async function ensurePersisted() {
    await upsertVisita(buildVisita());
    persisted.current = true;
  }

  async function handleCapture() {
    if (capturing) return;
    setErro(null);
    setCapturing(true);
    try {
      const foto = await tirarFoto();
      if (!foto) return;
      const capturadoEm = new Date().toISOString();
      const coords = await pegarLocalizacao();
      const talhoes = bundleQ.data?.talhoes ?? [];
      const tal = coords ? resolverTalhao(talhoes, coords.lon, coords.lat) : null;

      const est = tal
        ? estimarEstadio({
            dataEmergencia: tal.data_emergencia,
            dataPlantio: tal.data_plantio,
            dataVisita: capturadoEm,
            cicloDias: tal.ciclo_dias ?? null,
          })
        : null;
      const legenda = tal
        ? montarLegenda({ numero: tal.numero, variedade: tal.variedade, dataPlantio: tal.data_plantio, estadioInfo: est })
        : ['Talhão não identificado'];

      const clientUuid = crypto.randomUUID();
      await ensurePersisted();
      await addFoto({
        clientUuid,
        visitaUuid,
        blob: foto.blob,
        mime: foto.mime,
        filename: foto.filename,
        lat: coords?.lat ?? null,
        lon: coords?.lon ?? null,
        capturadoEm,
        talhaoId: tal?.talhao_id ?? null,
        talhaoNumero: tal?.numero ?? null,
        legenda,
        estadio: est?.estadio ?? null,
        recomendacao: '',
        synced: false,
        createdAt: Date.now(),
      });
      setFotos((prev) => [
        ...prev,
        {
          clientUuid,
          url: URL.createObjectURL(foto.blob),
          talhaoNumero: tal?.numero ?? null,
          legenda,
          estadio: est?.estadio ?? null,
          recomendacao: '',
          accuracy: coords?.accuracy ?? null,
          hasAudio: false,
          audioDurMs: null,
          synced: false,
        },
      ]);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao capturar foto');
    } finally {
      setCapturing(false);
    }
  }

  // Novo ponto de amostragem: pega GPS, resolve talhão e abre o formulário de
  // contagem (alimentado pelo catálogo MIP cacheado no bundle).
  async function handleNovoPonto() {
    if (capturing) return;
    setErro(null);
    setCapturing(true);
    try {
      const coords = await pegarLocalizacao();
      const talhoes = bundleQ.data?.talhoes ?? [];
      const tal = coords ? resolverTalhao(talhoes, coords.lon, coords.lat) : null;
      setPontoPendente({
        lat: coords?.lat ?? null,
        lon: coords?.lon ?? null,
        accuracy: coords?.accuracy ?? null,
        talhaoId: tal?.talhao_id ?? null,
        talhaoNumero: tal?.numero ?? null,
      });
      setAmostraOpen(true);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao obter localização');
    } finally {
      setCapturing(false);
    }
  }

  async function handleSaveAmostra(medicoes: Medicao[], observacao: string) {
    if (!pontoPendente) return;
    const clientUuid = crypto.randomUUID();
    await ensurePersisted();
    await addAmostra({
      clientUuid,
      visitaUuid,
      lat: pontoPendente.lat,
      lon: pontoPendente.lon,
      capturadoEm: new Date().toISOString(),
      observacao,
      talhaoId: pontoPendente.talhaoId,
      talhaoNumero: pontoPendente.talhaoNumero,
      fotoClientUuid: null,
      medicoes,
      synced: false,
      createdAt: Date.now(),
    });
    setAmostras((prev) => [
      ...prev,
      { clientUuid, talhaoNumero: pontoPendente.talhaoNumero, nContagens: medicoes.length, accuracy: pontoPendente.accuracy, synced: false },
    ]);
    setAmostraOpen(false);
    setPontoPendente(null);
  }

  function handleRecomendacao(clientUuid: string, text: string) {
    setFotos((prev) => prev.map((f) => (f.clientUuid === clientUuid ? { ...f, recomendacao: text } : f)));
    void updateFoto(clientUuid, { recomendacao: text });
  }

  // Recomendação por áudio: grava com MediaRecorder e anexa à foto na fila offline.
  async function handleGravarAudio(clientUuid: string) {
    if (!suportaGravacao()) { setErro('Gravação de áudio não suportada neste aparelho.'); return; }
    setErro(null);
    try {
      recRef.current = await iniciarGravacao();
      setGravandoUuid(clientUuid);
    } catch (e) {
      const name = e instanceof DOMException ? e.name : '';
      if (name === 'NotFoundError') setErro('Microfone não encontrado no aparelho.');
      else setErro('Microfone bloqueado. Toque em "Gravar" de novo; se persistir, libere o microfone nas permissões do app (Ajustes → Apps → Hub Ruralplan → Permissões).');
    }
  }
  async function handlePararAudio(clientUuid: string) {
    const rec = recRef.current;
    recRef.current = null;
    setGravandoUuid(null);
    if (!rec) return;
    const out = await rec.stop();
    if (!out) return;
    await updateFoto(clientUuid, { audioBlob: out.blob, audioMime: out.mime, audioFilename: out.filename, audioDurMs: out.durMs });
    setFotos((prev) => prev.map((f) => (f.clientUuid === clientUuid ? { ...f, hasAudio: true, audioDurMs: out.durMs } : f)));
  }

  async function handleRemove(clientUuid: string) {
    const f = fotos.find((x) => x.clientUuid === clientUuid);
    if (f) URL.revokeObjectURL(f.url);
    await removeFoto(clientUuid);
    setFotos((prev) => prev.filter((x) => x.clientUuid !== clientUuid));
  }

  async function handleSync() {
    setErro(null);
    await ensurePersisted();
    try {
      const res = await sync.mutateAsync(visitaUuid);
      setFotos((prev) => prev.map((f) => ({ ...f, synced: true })));
      setAmostras((prev) => prev.map((a) => ({ ...a, synced: true })));
      if (res.fotos_nao_resolvidas > 0) {
        setErro(`${res.fotos_nao_resolvidas} ponto(s)/foto(s) sem talhão identificado por GPS.`);
      }
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao sincronizar');
    }
  }

  const syncedVisitaId = sync.data?.visita_id ?? null;

  return (
    <Stack gap="lg">
      {/* Header da sessão */}
      <Card padding="lg">
        <Stack gap="md">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-text">
                {fazenda?.fazenda ?? (bundleQ.isLoading ? 'Carregando fazenda…' : 'Fazenda')}
              </h2>
              <p className="text-xs text-text-2">
                {fazenda?.produtor ?? '—'}
                {fazenda?.municipio ? ` · ${fazenda.municipio}${fazenda.uf ? '/' + fazenda.uf : ''}` : ''}
              </p>
              <p className="text-xs text-text-muted mt-0.5">
                {me?.name ?? '—'} · {new Date(dataVisita).toLocaleDateString('pt-BR')}
              </p>
            </div>
            <Chip tone={online ? 'ok' : 'neutral'}>
              {online ? <Cloud size={12} strokeWidth={2} /> : <CloudOff size={12} strokeWidth={2} />}
              {online ? 'Online' : 'Offline'}
            </Chip>
          </div>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-text-2">Observações gerais</span>
            <textarea
              className={textareaCls}
              rows={3}
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              onBlur={() => persisted.current && void ensurePersisted()}
              placeholder="Condições gerais da lavoura, clima, manejo…"
            />
          </label>
        </Stack>
      </Card>

      {/* Ações de captura */}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="primary" size="lg" onClick={() => void handleCapture()} disabled={capturing || bundleQ.isLoading}>
          {capturing ? <Loader2 size={18} strokeWidth={2} className="animate-spin" /> : <Camera size={18} strokeWidth={2} />}
          {capturing ? 'Capturando…' : 'Tirar foto'}
        </Button>
        <Button variant="secondary" size="lg" onClick={() => void handleNovoPonto()} disabled={capturing || bundleQ.isLoading}>
          <Crosshair size={18} strokeWidth={2} />
          Novo ponto de amostragem
        </Button>
        <span className="text-xs text-text-muted">
          {fotos.length} foto(s){pendentes > 0 ? ` · ${pendentes} pend.` : ''}
          {amostras.length > 0 ? ` · ${amostras.length} ponto(s)` : ''}
        </span>
      </div>

      {/* Pontos de amostragem registrados */}
      {amostras.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {amostras.map((a, i) => (
            <div key={a.clientUuid} className="flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2">
              <Chip tone={a.talhaoNumero ? 'accent' : 'warn'}>
                <MapPin size={11} strokeWidth={2} />
                {a.talhaoNumero ? `Talhão ${a.talhaoNumero}` : 'Sem talhão'}
              </Chip>
              <span className="text-sm text-text">Ponto {i + 1}</span>
              <span className="text-xs text-text-muted">
                {a.nContagens} contagem(ns)
                {a.accuracy != null ? ` · GPS ±${Math.round(a.accuracy)} m` : ''}
                {a.synced ? ' · sincronizado' : ''}
              </span>
            </div>
          ))}
        </div>
      )}

      {bundleQ.isError && (
        <p className="text-xs text-danger">Não foi possível carregar os talhões desta fazenda (online é necessário na primeira vez).</p>
      )}
      {erro && <p className="text-xs text-danger">{erro}</p>}

      {/* Galeria */}
      {fotos.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {fotos.map((f) => (
            <Card key={f.clientUuid} padding="none">
              <div className="relative">
                {/* src é valor dinâmico (objectURL) — permitido */}
                <img src={f.url} alt="" className="w-full h-44 object-cover rounded-t-[var(--radius-lg)]" />
                <div className="absolute top-2 left-2">
                  <Chip tone={f.talhaoNumero ? 'accent' : 'warn'}>
                    <MapPin size={11} strokeWidth={2} />
                    {f.talhaoNumero ? `Talhão ${f.talhaoNumero}` : 'Sem talhão'}
                  </Chip>
                </div>
              </div>
              <div className="p-3 flex flex-col gap-2">
                <div className="text-xs text-text-2 leading-relaxed">
                  {f.legenda.map((l, i) => (
                    <div key={i} className={i === 0 ? 'font-semibold text-text' : undefined}>{l}</div>
                  ))}
                </div>
                <textarea
                  className={textareaCls}
                  rows={2}
                  value={f.recomendacao}
                  onChange={(e) => handleRecomendacao(f.clientUuid, e.target.value)}
                  placeholder="Recomendação do agrônomo (opcional)…"
                />
                <div className="flex flex-wrap items-center gap-2">
                  {gravandoUuid === f.clientUuid ? (
                    <Button variant="danger" size="sm" onClick={() => void handlePararAudio(f.clientUuid)}>
                      <Square size={13} strokeWidth={2} /> Parar gravação
                    </Button>
                  ) : (
                    <Button variant="secondary" size="sm" disabled={f.synced || gravandoUuid != null} onClick={() => void handleGravarAudio(f.clientUuid)}>
                      <Mic size={13} strokeWidth={2} /> {f.hasAudio ? 'Regravar áudio' : 'Gravar recomendação (áudio)'}
                    </Button>
                  )}
                  {f.hasAudio && gravandoUuid !== f.clientUuid && (
                    <span className="text-[11px] text-ok">Áudio gravado{f.audioDurMs ? ` · ${Math.round(f.audioDurMs / 1000)}s` : ''}</span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-text-muted">
                    {f.accuracy != null ? `GPS ±${Math.round(f.accuracy)} m` : 'Sem GPS'}
                    {f.synced ? ' · sincronizada' : ''}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => void handleRemove(f.clientUuid)} disabled={f.synced}>
                    <Trash2 size={14} strokeWidth={2} />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Sincronização */}
      <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
        <Button variant="primary" onClick={() => void handleSync()} disabled={!online || (pendentes === 0 && amostrasPendentes === 0) || sync.isPending}>
          {sync.isPending ? <Loader2 size={16} strokeWidth={2} className="animate-spin" /> : <Cloud size={16} strokeWidth={2} />}
          {sync.isPending
            ? 'Sincronizando…'
            : `Sincronizar ${pendentes} foto(s)${amostrasPendentes > 0 ? ` + ${amostrasPendentes} ponto(s)` : ''}`}
        </Button>
        {!online && <span className="text-xs text-text-muted">Conecte-se para sincronizar — as fotos ficam salvas no aparelho.</span>}
        {syncedVisitaId && (
          <Link to="/agronomia/visita/$id" params={{ id: String(syncedVisitaId) }}>
            <Button variant="secondary">
              <FileText size={16} strokeWidth={2} />
              Ver relatório
            </Button>
          </Link>
        )}
        {syncedVisitaId && (
          <a href={visitaRelatorioPdfUrl(syncedVisitaId)} target="_blank" rel="noreferrer">
            <Button variant="ghost">PDF</Button>
          </a>
        )}
      </div>

      <AmostraForm
        open={amostraOpen}
        onClose={() => { setAmostraOpen(false); setPontoPendente(null); }}
        catalogo={catalogo}
        talhaoNumero={pontoPendente?.talhaoNumero ?? null}
        onSave={(medicoes, observacao) => void handleSaveAmostra(medicoes, observacao)}
      />
    </Stack>
  );
}
